import * as React from "react";
import styled, { css } from "styled-components";
import { select, useSelect } from "@hubper/theme";

import { inputResetStyle } from "../../elements/TextInput";
import Icon from "../../elements/Icon";
import { animated, useSpring } from "react-spring";

import posed, { PoseGroup } from "react-pose";

import mergeHandlers from "../../util/mergeHandlers";
import mergeStyles from "../../util/mergeStyles";

const IconWrapper = posed.div({
  enter: {
    opacity: 1,
    x: 0,
    y: "-50%",
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 50
    }
  },
  exit: {
    opacity: 0,
    x: 24,
    y: "-50%",
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 50
    }
  }
});

const iconStyle: React.CSSProperties = {
  position: "absolute",
  top: "50%"
};

const Base = styled.div`
  display: flex;
  height: 30px;
  align-items: center;
  padding-left: ${select.space(1)};
  padding-right: ${select.space(1)};
  position: relative;
  border-width: 0px;
  border-bottom-width: 1px;
  border-style: solid;
`;

const phBase = css`
  color: ${select.color(["grey", 2])};
`;

const inputPlaceholderStyle = css`
  ::-webkit-input-placeholder {
    ${phBase}
  }
  ::-moz-placeholder {
    ${phBase}
  }
  :-ms-input-placeholder {
    ${phBase}
  }
  :-moz-placeholder {
    ${phBase}
  }
`;

const Input = styled.input`
  ${inputResetStyle}
  ${inputPlaceholderStyle}
  font-family: ${select.font("Lato")};
  flex: 1;
  height: 100%;
`;

const Line = styled(animated.div)`
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  height: 1px;
`;

type Props = {
  style?: React.CSSProperties;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  value?: string;
} & React.HTMLAttributes<HTMLInputElement>;

function getBorderStyle(
  isHovered: boolean,
  hasFocus: boolean,
  disabled: boolean,
  color: any
) {
  if (hasFocus && !disabled) {
    return `linear-gradient(to right, ${color(["grey", 6])} 100%, ${color([
      "grey",
      3
    ])} 100%)`;
  }

  if (isHovered && !disabled) {
    return `linear-gradient(to right, ${color(["grey", 4])} 0%, ${color([
      "grey",
      4
    ])} 1%)`;
  }

  return `linear-gradient(to right, ${color(["grey", 2])} 0%, ${color([
    "grey",
    2
  ])} 1%)`;
}

function AdminSearchbar(
  { style, className, placeholder, value, disabled, children, ...rest }: Props,
  ref: any
) {
  const { space, color } = useSelect();

  const [isHovered, setHover] = React.useState(false);
  const [hasFocus, setFocus] = React.useState(false);

  const bgStyle = useSpring({
    background: getBorderStyle(isHovered, hasFocus, disabled!, color),
    config: {
      tension: 200
    }
  });

  return (
    <Base
      ref={ref}
      style={mergeStyles(
        {
          width: 250,
          backgroundColor: disabled ? color(["grey", 0]) : "white"
        },
        style
      )}
      className={className}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Input
        placeholder={placeholder}
        {...rest}
        value={value}
        disabled={disabled}
        onFocus={mergeHandlers(rest.onFocus, () => setFocus(true))}
        onBlur={mergeHandlers(rest.onBlur, () => setFocus(false))}
      />
      <PoseGroup flipMove={false}>
        {!value ? (
          <IconWrapper
            key="search"
            style={{
              ...iconStyle,
              right: space(1)
            }}
          >
            <Icon name="search" size={24} c={["grey", 3]} />
          </IconWrapper>
        ) : (
          <IconWrapper
            key="close"
            style={{
              ...iconStyle,
              right: space(1),
              cursor: "pointer"
            }}
            onMouseDown={evt => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            onClick={() => {
              if (rest.onChange) {
                (rest as any).onChange({ target: { value: "" } });
              }
            }}
          >
            <Icon name="close" size={20} c={["grey", 2]} />
          </IconWrapper>
        )}
      </PoseGroup>

      <Line
        style={{
          ...bgStyle
        }}
      />
    </Base>
  );
}

export default React.forwardRef(AdminSearchbar);
